/**
 * AntD ConfigProvider theme (R-18.18 / R-18.129): the theme is defined here from the
 * lifted design tokens, NEVER by hardcoding colour values in components. Light + dark
 * are produced via AntD's algorithms — dark mode uses `theme.darkAlgorithm` (R-18.22
 * INTENT; the Tailwind `dark:` sync clause is a scoped non-applicability for this
 * AntD-only stack — see spec Open issues 1 & 2 / DEC-025).
 *
 * The sider stays dark in BOTH modes (DEC-025): the light theme deliberately sets the
 * Layout `siderBg` to the dark sider colour and feeds the dark Menu tokens so the
 * navigation rail keeps its dark surface under the default (light) algorithm.
 */

import { theme as antdTheme, type ThemeConfig } from 'antd';

import { palette, regions } from './tokens';

// Shared brand + status scale (R-18.19): primary, success, warning, error.
const sharedToken: ThemeConfig['token'] = {
  colorPrimary: palette.primary,
  colorSuccess: palette.success,
  colorWarning: palette.warning,
  colorError: palette.danger,
};

export const darkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    ...sharedToken,
    colorBgLayout: regions.dark.body,
    colorBgContainer: regions.dark.content,
    colorBgElevated: regions.dark.header,
    colorText: regions.dark.text,
  },
  components: {
    Layout: {
      headerBg: regions.dark.header,
      bodyBg: regions.dark.body,
      siderBg: regions.dark.sider,
    },
    Menu: {
      darkItemBg: regions.dark.sider,
      darkSubMenuItemBg: regions.dark.sider,
    },
    Table: {
      headerBg: regions.dark.tableHeader,
      rowHoverBg: regions.dark.tableRowHover,
    },
  },
};

export const lightTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    ...sharedToken,
    colorBgLayout: regions.light.body,
    colorBgContainer: regions.light.content,
    colorBgElevated: regions.light.header,
    colorText: regions.light.text,
  },
  components: {
    Layout: {
      headerBg: regions.light.header,
      bodyBg: regions.light.body,
      // Sider stays dark in light mode (DEC-025).
      siderBg: regions.light.sider,
    },
    Menu: {
      // Feed the dark Menu tokens so the sider menu stays legible on the dark rail.
      darkItemBg: regions.light.sider,
      darkSubMenuItemBg: regions.light.sider,
    },
    Table: {
      headerBg: regions.light.tableHeader,
    },
  },
};

export const themes = {
  dark: darkTheme,
  light: lightTheme,
} as const;
